import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, CheckCircle2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import PageWrapper from "../components/PageWrapper";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const ContactInfo = ({ icon: Icon, title, lines, testId }) => (
  <div className="flex items-start gap-4" data-testid={testId}>
    <div className="shrink-0 w-12 h-12 rounded-2xl bg-blue-50 text-[#2563EB] flex items-center justify-center">
      <Icon size={20} />
    </div>
    <div>
      <div className="font-bold text-[#0F1B2D] mb-1">{title}</div>
      {lines.map((l) => (
        <div key={l} className="text-[14px] text-slate-600">
          {l}
        </div>
      ))}
    </div>
  </div>
);

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    school: "",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    // Simulate frontend-only submission
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
      toast.success("Thanks! Our team will reach out within 24 hours.");
      setForm({ name: "", email: "", school: "", phone: "", message: "" });
      setTimeout(() => setSubmitted(false), 4000);
    }, 700);
  };

  return (
    <PageWrapper testId="contact-page">
      <Toaster position="top-center" richColors />
      <section
        className="hero-panel-bg pt-10 pb-12"
        data-testid="contact-hero"
      >
        <div className="max-w-[1280px] mx-auto px-5 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <div className="inline-flex items-center gap-2 bg-blue-50 text-[#2563EB] font-semibold px-4 py-2 rounded-full text-sm mb-5">
              We'd Love to Hear From You
            </div>
            <h1 className="brand-headline text-4xl sm:text-5xl lg:text-[3.4rem] mb-5">
              Let's get your school <br />
              transport <span className="brand-blue">sorted</span>
              <span className="text-amber-500">.</span>
            </h1>
            <p className="text-slate-600 text-base leading-relaxed">
              Tell us about your school and current transport needs. Our team
              will reach out with a tailored plan, demo of our parent app, and
              transparent pricing — typically within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-[1280px] mx-auto px-5 lg:px-10 mb-14">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 bg-white rounded-3xl border border-slate-200 p-7 sm:p-10 shadow-[0_20px_60px_-30px_rgba(15,27,45,0.18)]"
            data-testid="contact-form-card"
          >
            <h2 className="text-2xl font-extrabold text-[#0F1B2D] mb-2">
              Send us a message
            </h2>
            <p className="text-slate-500 text-sm mb-6">
              We'll respond within one business day.
            </p>

            <form
              onSubmit={handleSubmit}
              className="space-y-5"
              data-testid="contact-form"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">
                    Full Name *
                  </label>
                  <Input
                    value={form.name}
                    onChange={update("name")}
                    placeholder="e.g. Aarav Sharma"
                    className="h-11 rounded-xl border-slate-200 focus-visible:ring-[#2563EB]"
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">
                    Email *
                  </label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@school.edu"
                    className="h-11 rounded-xl border-slate-200 focus-visible:ring-[#2563EB]"
                    data-testid="contact-input-email"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">
                    School Name
                  </label>
                  <Input
                    value={form.school}
                    onChange={update("school")}
                    placeholder="Greenfield International School"
                    className="h-11 rounded-xl border-slate-200 focus-visible:ring-[#2563EB]"
                    data-testid="contact-input-school"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">
                    Phone
                  </label>
                  <Input
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+91 98xxxxxx"
                    className="h-11 rounded-xl border-slate-200 focus-visible:ring-[#2563EB]"
                    data-testid="contact-input-phone"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">
                  Message *
                </label>
                <Textarea
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about your school size, current routes, and any specific requirements…"
                  className="rounded-xl border-slate-200 focus-visible:ring-[#2563EB]"
                  data-testid="contact-input-message"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="h-12 px-7 bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-semibold rounded-xl text-[15px] gap-2 disabled:opacity-70"
              >
                {submitted ? (
                  <>
                    <CheckCircle2 size={18} /> Message Sent
                  </>
                ) : submitting ? (
                  "Sending…"
                ) : (
                  <>
                    Send Message <Send size={16} />
                  </>
                )}
              </Button>
            </form>
          </motion.div>

          {/* Info */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-5 space-y-7"
          >
            <div className="bg-[#0F1B2D] text-white rounded-3xl p-7 sm:p-9 relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-amber-400/20 blur-2xl" />
              <h3 className="text-xl font-extrabold mb-2">
                Quick Connect
              </h3>
              <p className="text-slate-300 text-sm mb-6">
                Reach out directly — we're available Monday to Saturday, 9 AM
                to 7 PM IST.
              </p>

              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-amber-300">
                    <Phone size={18} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400">
                      Call us
                    </div>
                    <div className="font-bold">7900189196</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-amber-300">
                    <Mail size={18} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400">
                      Email us
                    </div>
                    <div className="font-bold">mdsttravels@gmail.com</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-amber-300">
                    <MapPin size={18} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400">
                      Visit us
                    </div>
                    <div className="font-bold leading-snug">
                      FLAT,504, NAV-HRISHIKESH, CO OP SOC LTD BLDG NO 15, MIRA ROAD,<br />
                      THANE, THANE- 401107, MAHARASHTRA
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl border border-slate-200 p-7">
              <h3 className="text-lg font-extrabold text-[#0F1B2D] mb-3">
                What happens next?
              </h3>
              <ol className="space-y-3 text-[14px] text-slate-600">
                {[
                  "We review your enquiry and assign a school transport specialist.",
                  "A 30-min discovery call to understand routes, students and goals.",
                  "Customised proposal with pricing, timelines and pilot plan.",
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-[#2563EB] text-white text-xs font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          </motion.div>
        </div>
      </section>
    </PageWrapper>
  );
};

export default Contact;
